import type { ProductListingDetail, VariantOption } from "../types/ProductListingDetail";
import type { ProductWizardData } from "../hooks/useProductWizard";
import type { ProductVariant, MarketplaceShipper } from "../types/ProductListing";
import type { Shipper, ShipperHierarchy, ApiShipper } from "../../shippers";

/**
 * Convert weight to grams for API submission
 * API expects weight in grams
 */
export function convertWeightToGrams(weight: number, unit: 'kg' | 'g' = 'g'): number {
  if (unit === 'kg') {
    return weight * 1000;  // Convert kg to grams
  }
  return weight;  // Already in grams
}

/**
 * Transform API variant_options to wizard format
 * API: { name: "color_family", option_list: [{ option: "Black", image: "..." }, ...] }
 * Wizard: { id, name, type, values: [{ id, value, displayName, imageUrl }], required, sortOrder }
 */
function transformVariantOptions(apiOptions: VariantOption[]): any[] {
  if (!apiOptions || apiOptions.length === 0) return [];

  return apiOptions.map((opt, index) => ({
    id: opt.id,
    name: opt.name,  // "color_family", "size", etc.
    type: 'dropdown',  // Default type for marketplace options
    values: opt.option_list.map(item => ({
      id: item.option.toLowerCase().replace(/\s+/g, '_'),  // Generate valueId
      value: item.option,  // "Black", "Silver", etc.
      displayName: item.option,
      imageUrl: item.image
    })),
    required: true,  // Marketplace options are typically required
    sortOrder: opt.index ?? index
  }));
}

/**
 * Helper: Map shipper name to Material icon
 */
function getShipperIcon(name: string): string {
  const nameLower = name.toLowerCase();
  if (nameLower.includes('jne') || nameLower.includes('j&t') || nameLower.includes('cargo')) {
    return 'local_shipping';
  }
  if (nameLower.includes('pos')) {
    return 'local_post_office';
  }
  return 'local_shipping'; // Default icon
}

/**
 * Helper: Map shipper name to Tailwind color class
 */
function getShipperColor(name: string): string {
  const nameLower = name.toLowerCase();
  if (nameLower.includes('jne')) return 'red-600';
  if (nameLower.includes('j&t')) return 'blue-600';
  if (nameLower.includes('pos')) return 'yellow-600';
  if (nameLower.includes('sicepat')) return 'green-600';
  return 'gray-600'; // Default color
}

/**
 * Transform marketplace shippers with grouping by name pattern
 * Groups by name pattern matching (e.g., "J&T Cargo" + "J&T Express" → parent "J&T")
 *
 * @param availableShippers - All marketplace shippers from API
 * @param enabledShippers - Shippers enabled for this product (from product listing API)
 * @returns Record with parent shippers containing children arrays
 */
function transformShippers(
  availableShippers: MarketplaceShipper[],
  enabledShippers: any[]
): Record<string, Shipper> {
  console.log('=== transformShippers Debug ===');
  console.log('Available Marketplace Shippers:', availableShippers);
  console.log('Product Enabled Shippers:', enabledShippers);

  if (!availableShippers || availableShippers.length === 0) {
    console.log('No available shippers - returning empty object');
    return {};
  }

  // Create set of enabled shipper IDs from product listing API
  const enabledShipperIds = new Set(
    (enabledShippers || []).map(s => s.id)
  );
  console.log('Enabled Shipper IDs:', Array.from(enabledShipperIds));

  // De-duplicate by logistic_channel_id (keep first occurrence)
  const seenChannelIds = new Set<string>();
  const deduplicatedShippers = availableShippers.filter(ms => {
    if (!ms.is_active) return false;  // Skip inactive shippers

    const channelId = ms.logistic_channel_id;
    if (channelId && seenChannelIds.has(String(channelId))) {
      console.log(`[transformShippers] Skipping duplicate channel ID ${channelId}: ${ms.logistic_channel_name}`);
      return false;
    }
    if (channelId) seenChannelIds.add(String(channelId));
    return true;
  });

  // Convert API marketplace shippers to shipper format
  // Mark as enabled if ID matches product's enabled shippers
  const mergedShippers = deduplicatedShippers.map(ms => ({
    id: ms.id,
    name: ms.logistic_channel_name,
    // Check if this shipper's ID is in the product's enabled shippers list
    enabled: enabledShipperIds.has(ms.id),
    fee_type: 'marketplace', // Marketplace shippers are always marketplace type
    cod_enabled: ms.cod_enabled,
    weight_limits: ms.weight_limits,
    dimension_limits: ms.dimension_limits,
  }));

  console.log('Merged shippers before grouping:', mergedShippers);

  // Group by name pattern
  const grouped = groupShippersByNamePattern(mergedShippers);

  console.log('Grouped shippers result:', grouped);

  return grouped;
}

/**
 * Transform API variant_combination to wizard format
 * Maps tier_variation to both options and optionValues using variant_options data
 */
function transformVariantCombination(
  variant: ProductVariant,
  variantOptions: VariantOption[]
): any {
  const options: Record<string, string> = {};
  const optionValues: Record<string, any> = {};
  let variantImageUrl: string | undefined = variant.photo; // Start with variant's own photo

  // Build a map of option names to option IDs for quick lookup
  const optionNameToId = new Map<string, VariantOption>();
  variantOptions.forEach(opt => {
    optionNameToId.set(opt.name, opt);
  });

  // Process each tier in tier_variation
  variant.tierVariation?.forEach((tier) => {
    const optionName = Object.keys(tier)[0];  // e.g., "color_family"
    const optionValue = tier[optionName];      // e.g., "Black"

    // Find the matching variant option
    const variantOption = optionNameToId.get(optionName);
    if (variantOption) {
      const optionId = variantOption.id;  // Use the option ID as key

      // Find the matching value in option_list
      const matchingValue = variantOption.option_list.find(
        item => item.option === optionValue
      );

      if (matchingValue) {
        // Use lowercase option value as valueId for consistency
        const valueId = optionValue.toLowerCase().replace(/\s+/g, '_');

        // Set simple options mapping
        options[optionId] = valueId;

        // Set full optionValues mapping
        optionValues[optionId] = {
          id: valueId,
          value: optionValue,
          displayName: optionValue,
          imageUrl: matchingValue.image
        };

        // Use the first option value's image as the variant image if no photo exists
        // This is typically the case for color options
        if (!variantImageUrl && matchingValue.image) {
          variantImageUrl = matchingValue.image;
        }
      }
    }
  });

  return {
    id: variant.id,
    sku: variant.sku,
    variantId: variant.variantId,
    options: options,  // { "option-id": "value-id" }
    optionValues: optionValues,  // { "option-id": { id, value, displayName, imageUrl } }
    price: variant.price.current,
    originalPrice: variant.price.original,
    stock: variant.stock,
    imageUrl: variantImageUrl,  // Use option value image or variant photo
    weight: variant.weight,
    dimension: variant.dimension,
    isActive: variant.isActive,
    isMainVariant: variant.isMainVariant,
    warehouseStock: variant.inventoryAllocation || {},  // Map API field to component field for VariantOptionsBuilder
    inventoryAllocation: variant.inventoryAllocation,    // Keep for backward compatibility
  };
}

/**
 * Transforms the detailed product data from the API into the format
 * expected by the Product Wizard component.
 *
 * @param detail - The detailed product data fetched from the API.
 * @param availableShippers - Available marketplace shippers from API
 * @returns The data structured for the wizard's steps.
 */
export function transformDetailToWizardData(
  detail: ProductListingDetail,
  availableShippers: MarketplaceShipper[]
): ProductWizardData {
  // No need to extract categoryId - it's derived from initialCategoryPath using getCategoryIdFromPath helper

  const wizardData: ProductWizardData = {
    basicInfo: {
      name: detail.title,
      description: detail.description || "",
      selectedStore: detail.accountId, // Use accountId for the store selection
      masterSku: detail.sku || detail.merchantProduct?.sku || "", // Fallback: detail.sku → merchant product SKU → empty string
      images: (detail.photos || []).map(p => ({
        id: p.id || p.url,  // Use photo ID if available, fallback to URL
        url: p.url,
        isMain: p.is_primary,
      })),
      linkedMerchantProduct: detail.merchantProduct || null,
    },
    category: {
      initialCategoryPath: detail.category || [], // Pass the entire category array for breadcrumb
      // categoryId removed - use getCategoryIdFromPath(initialCategoryPath) instead
      brandId: detail.brandName || "", // Assuming brandName is used for brandId for now
      attributes: detail.savedAttributeValues || {},  // Use saved values directly
    },
    salesInfo: {
      hasVariants: (detail.variantCombinations || []).length > 0,
      basePrice: detail.price.current,
      variantOptions: transformVariantOptions(detail.variantOptions || []), // Transform variant options
      variants: (detail.variantCombinations || []).map(v =>
        transformVariantCombination(v, detail.variantOptions || [])
      ), // Transform each variant with variant options
      inventory: {
        quantity: detail.totalStock,
      },
    },
    shipping: {
      dimensions: {
        // API stores weight in grams
        weight: detail.weight || 1,  // Weight in grams from API
        weightUnit: 'g' as const,  // Default unit is grams
        length: detail.dimension?.length || 1,
        width: detail.dimension?.width || 1,
        height: detail.dimension?.height || 1,
        dimensionUnit: 'cm' as const,
      },
      shippers: transformShippers(availableShippers, detail.shippers || []),  // Use API marketplace shippers
      shippersLoaded: true,  // Mark as loaded - shippers fetched successfully (even if empty)
      isDangerousProduct: detail.isDangerousProduct || false,  // New field from API
    },
    review: {},
  };

  return wizardData;
}

/**
 * Group shippers by name pattern to create parent-child hierarchy
 * Example: "J&T Cargo" + "J&T Express" → parent "J&T" with children
 */
function groupShippersByNamePattern(shippers: any[]): Record<string, Shipper> {
  const grouped = new Map<string, any[]>();

  shippers.forEach(shipper => {
    const prefix = extractCompanyPrefix(shipper.name);
    if (!grouped.has(prefix)) {
      grouped.set(prefix, []);
    }
    grouped.get(prefix)!.push(shipper);
  });

  const result: Record<string, Shipper> = {};

  grouped.forEach((children, parentName) => {
    if (children.length === 1) {
      // Single shipper - no parent-child grouping needed
      const shipper = children[0];
      result[shipper.id] = {
        id: shipper.id,
        name: shipper.name,
        enabled: shipper.enabled,
        feeType: shipper.fee_type,
        isParent: false,
        icon: getShipperIcon(shipper.name),
        color: getShipperColor(shipper.name)
      };
    } else {
      // Multiple shippers with same prefix - create parent with children
      const parentId = `parent_${parentName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      result[parentId] = {
        id: parentId,
        name: parentName,
        enabled: children.some(c => c.enabled),
        isParent: true,
        icon: getShipperIcon(parentName),
        color: getShipperColor(parentName),
        children: children.map(child => ({
          id: child.id,
          name: child.name,
          enabled: child.enabled,
          feeType: child.fee_type,
          isParent: false,
          parentId: parentId,
          parentName: parentName,
          icon: getShipperIcon(child.name),
          color: getShipperColor(child.name)
        }))
      };
    }
  });

  return result;
}

/**
 * Extract company prefix from shipper name for grouping
 * Examples: "J&T Cargo" → "J&T", "JNE Regular" → "JNE"
 */
function extractCompanyPrefix(shipperName: string): string {
  // Normalize the name for matching (case-insensitive)
  const normalized = shipperName.toLowerCase();

  // Special handling for Shopee services (keep separate)
  if (normalized.startsWith('shopee xpress')) return 'Shopee Xpress';
  if (normalized.startsWith('agen shopee')) return 'Agen Shopee';
  if (normalized.startsWith('spx express') || normalized.startsWith('agen spx')) return 'SPX Express';
  if (normalized.startsWith('spx ')) return 'SPX';

  // Match common courier prefixes
  const courierPatterns = [
    { regex: /^j&t\s/i, name: 'J&T' },
    { regex: /^jne\s/i, name: 'JNE' },
    { regex: /^sicepat\s/i, name: 'SiCepat' },
    { regex: /^anteraja\s/i, name: 'Anteraja' },
    { regex: /^gosend\s/i, name: 'GoSend' },
    { regex: /^grabexpress\s/i, name: 'GrabExpress' },
    { regex: /^grab\s/i, name: 'Grab' },
    { regex: /^pos\s/i, name: 'Pos' },
    { regex: /^ninja\s/i, name: 'Ninja' },
    { regex: /^tiki\s/i, name: 'TIKI' },
    { regex: /^indopaket\s/i, name: 'Indopaket' },
    { regex: /^alfatrex\s/i, name: 'Alfatrex' },
    { regex: /^bluebird\s/i, name: 'Bluebird' },
    { regex: /^id express/i, name: 'ID Express' },
  ];

  for (const { regex, name } of courierPatterns) {
    if (regex.test(shipperName)) return name;
  }

  // Fallback: extract first word before space
  const fallbackMatch = shipperName.match(/^([A-Za-z&]+)\s+/);
  if (fallbackMatch) return fallbackMatch[1];

  // No pattern matched and no space found - return full name (will be standalone)
  return shipperName;
}
